import React, {
  ChangeEvent,
  ForwardedRef,
  FunctionComponent,
  Ref,
  useEffect,
  useRef,
  useState,
} from 'react';

import styled from 'styled-components';

import Input, { InputType, onAutoFillCancel, onAutoFillStart } from './Input';
import Label from './Label';
import Wiggler from './Wiggler';

type TextFieldType = InputType & {
  label: string;
  name?: string;
  id: string;
  ref?: Ref<HTMLInputElement>;
  onFocus?: React.FocusEventHandler;
  onBlur?: React.ChangeEventHandler;
  onChange?: React.ChangeEventHandler;
  onAutoFill?: React.FocusEventHandler<HTMLInputElement>;
};

type TextFieldProps = TextFieldType & React.HTMLProps<HTMLInputElement>;

const ForwardedTextField: FunctionComponent<TextFieldProps> = React.forwardRef(
  function TextField(
    {
      label,
      name,
      id,
      hasError = false,
      onFocus,
      onBlur,
      onChange,
      onAutoFill,
      onInput,
      onAnimationStart,
      ...props
    },
    ref: ForwardedRef<any>
  ) {
    const [isFocused, setIsFocus] = useState<boolean>(false);
    const [isEmpty, setIsEmpty] = useState<boolean>(true);
    const innerRef = useForwardedRef(ref);
    const inputValueLength = innerRef.current?.value.length;

    useEffect(() => {
      // Ref has to be accessed again on load to ensure we get the initial provided value
      const inputValueLength = innerRef.current?.value.length;
      if (inputValueLength > 0 && isEmpty) {
        setIsEmpty(false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      if (inputValueLength > 0 && isEmpty) {
        if (onChange) {
          onChange({ target: innerRef.current } as ChangeEvent);
        }
        setIsEmpty(false);
      }
    }, [isEmpty, onChange, inputValueLength, innerRef]);

    const handleFocus: React.FocusEventHandler<HTMLInputElement> = event => {
      if (onFocus) {
        onFocus(event);
      }
      setIsFocus(true);
    };

    const handleBlur: React.ChangeEventHandler<HTMLInputElement> = event => {
      if (onBlur) {
        onBlur(event);
      }
      setIsFocus(false);
    };

    const handleChange: React.ChangeEventHandler<HTMLInputElement> = event => {
      if (onChange) {
        onChange(event);
      }

      if (event.target.value.length === 0 && !isEmpty) {
        setIsEmpty(true);
      } else if (isEmpty) {
        setIsEmpty(false);
      }
    };

    const handleAnimationStart = e => {
      if (onAnimationStart) {
        onAnimationStart(e);
      }

      if (!onAutoFill) return;

      if (e.animationName === onAutoFillStart.name) {
        autoComplete(e.target as HTMLInputElement, onAutoFill);
      } else if (e.animationName === onAutoFillCancel.name) {
        cancelAutoComplete(e.target as HTMLInputElement, onAutoFill);
      }
    };

    const handleInput = e => {
      if (onInput) {
        onInput(e);
      }

      if (!onAutoFill) return;

      const { nativeEvent } = e;

      if (
        'insertReplacementText' === nativeEvent.inputType ||
        !('data' in nativeEvent)
      ) {
        autoComplete(e.target as HTMLInputElement, onAutoFill);
      } else {
        cancelAutoComplete(e.target as HTMLInputElement, onAutoFill);
      }
    };

    return (
      <InputWrap>
        <Wiggler doWiggle={hasError}>
          <Input
            ref={innerRef}
            name={name}
            id={id}
            hasError={hasError}
            onBlur={handleBlur}
            onFocus={handleFocus}
            onChange={handleChange}
            onAnimationStart={handleAnimationStart}
            onInput={handleInput}
            {...props}
          />
          <Label htmlFor={id} isEmpty={isEmpty} isFocused={isFocused}>
            {label}
          </Label>
        </Wiggler>
      </InputWrap>
    );
  }
);

export default ForwardedTextField;

const InputWrap = styled.div`
  position: relative;
  /**
   we gotta set the height here also because of older Safari
   otherwise position relative will expand to the whole container height 
   */
  height: 4rem;
  min-width: 90px;
  flex: 1 1 30%;
`;

export type AutofillEvent = Event & {
  bubbles: boolean;
  cancelable: boolean;
  detail: {
    autoFilled: boolean;
  };
};

function autoComplete(element: HTMLInputElement, callback): void {
  if (element.hasAttribute('autofilled')) return;
  element.setAttribute('autofilled', 'true');

  const event: AutofillEvent = new window.CustomEvent('autofill', {
    bubbles: true,
    cancelable: true,
    detail: {
      autoFilled: true,
    },
  });

  // no autofill if preventDefault is called
  if (!element.dispatchEvent(event)) {
    element.value = '';
  }

  if (callback) {
    callback(event);
  }
}

function cancelAutoComplete(element: HTMLInputElement, callback): void {
  if (!element.hasAttribute('autofilled')) return;
  element.removeAttribute('autofilled');

  const event: AutofillEvent = new window.CustomEvent('autofill', {
    bubbles: true,
    cancelable: false,
    detail: {
      autoFilled: false,
    },
  });

  // dispatch event
  element.dispatchEvent(event);

  if (callback) {
    callback(event);
  }
}

function useForwardedRef(ref) {
  const innerRef = useRef(null);

  useEffect(() => {
    if (!ref) return;
    if (typeof ref === 'function') {
      ref(innerRef.current);
    } else {
      ref.current = innerRef.current;
    }
  }, [ref]);

  return innerRef;
}
